import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler } from '@app/store/actions';
import { ModalActionsComponent, PasslySelect } from '@app/components';
import { genericErrorString, standardLicenseTypeName, premiumLicenseTypeName } from '@app/helpers';
import { tenantsService } from '@app/services';
import { AxiosError } from 'axios';
import { TranslationsTypes, ITenantLicenses, ITenantsLicenseEntries } from '@app/models';
import moment from 'moment';
import { Calendar, InputNumber } from '@itglue/kaseya-ui';

export interface IReceivedData {
    dialogOpen: Function;
    tenantId: string;
    license: ITenantLicenses;
}

export interface ISelectValues {
    label: TranslationsTypes;
    value: string;
}

export const EditTenantLicenseDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const minNumberInput: number = 0;
    const maxNumberInput: number = 1000000;

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [formInvalid, setFormInvalid] = useState<boolean>(false);
    const [licenseState, setLicenseState] = useState<string>(props.license.State);
    const [standardSeats, setStandardSeats] = useState<number>(0);
    const [standardSeatsError, setStandardSeatsError] = useState<boolean>(false);
    const [premiumSeats, setPremiumSeats] = useState<number>(0);
    const [premiumSeatsError, setPremiumSeatsError] = useState<boolean>(false);
    const [organizationLimit, setOrganizationLimit] = useState<number>(props.license.OrganizationLimit);
    const [organizationLimitError, setOrganizationLimitError] = useState<boolean>(false);
    const [expirationDate, setExpirationDate] = useState<Date>(new Date(props.license.Expiry));
    const [dateInvalid, setDateInvalid] = useState<boolean>(false);

    const licenseStates: ISelectValues[] = [
        { label: 'TENANT_LICENSE_STATE_TRIAL', value: 'Trial' },
        { label: 'TENANT_LICENSE_STATE_ACTIVE', value: 'Active' },
        { label: 'TENANT_LICENSE_STATE_INACTIVE', value: 'Disabled' },
        { label: 'TENANT_LICENSE_STATE_CANCELED', value: 'Canceled' },
        { label: 'TENANT_LICENSE_STATE_NOTFORRESALE', value: 'NotForResale' }
    ];

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        props.license.Entries.map(function (value: ITenantsLicenseEntries, index: number) {
            if (value.Name === standardLicenseTypeName) {
                setStandardSeats(value.Seats);
            }
            else if (value.Name === premiumLicenseTypeName) {
                setPremiumSeats(value.Seats);
            }
        });
    }, []);

    useEffect(() => {
        if (standardSeatsError || premiumSeatsError || organizationLimitError || dateInvalid) {
            setFormInvalid(true);
        }
        else {
            setFormInvalid(false);
        }
    }, [standardSeatsError, premiumSeatsError, organizationLimitError, dateInvalid]);

    const handleSaveBtn = (e: any) => {
        tenantsService.editLicense(props.tenantId, licenseState, expirationDate, organizationLimit, standardSeats, premiumSeats,
            handleError, handleEditTenantLicenseSuccess);
    };

    const handleError = (response: AxiosError<any>) => {
        const error: string = genericErrorString(response);

        setErrorMessage(error);
    };

    const validateNumberField = (value: number): boolean => {
        return isNaN(value) || value < minNumberInput || value > maxNumberInput;
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'licenseState':
                setLicenseState(value);
                break;
            case 'standardSeats':
                setStandardSeats(value);
                setStandardSeatsError(validateNumberField(parseInt(value)));
                break;
            case 'premiumSeats':
                setPremiumSeats(value);
                setPremiumSeatsError(validateNumberField(parseInt(value)));
                break;
            case 'organizationLimit':
                setOrganizationLimit(value);
                setOrganizationLimitError(validateNumberField(parseInt(value)));
                break;
            default:
                break;
        }
    };

    const handleDateChange = (e: any) => {
        if (e != null) {
           if (moment(e.target.value).isValid()) {
               setExpirationDate(e.target.value);
               setDateInvalid(false);
            }
            else {
                setDateInvalid(true);
            }
        }
        else {
            setDateInvalid(true);
        }
    };

    const handleEditTenantLicenseSuccess = () => {
        props.dialogOpen(true);
    };

    const handleCancel = () => {
        props.dialogOpen(false);
    };

    return (
        <>
            <form method='post' className='modal__form' tabIndex={0} onKeyPress={(e: React.KeyboardEvent<HTMLFormElement>) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveBtn(e);
                }
            }}>
                <div className='modal__body--row'>
                    <PasslySelect
                        label='TENANT_LICENSE_STATE'
                        name='licenseState'
                        value={licenseState}
                        onChange={onFormChange}
                        id='licenseState-input'
                        fullWidth={true}
                        options={licenseStates}
                    />
                </div>
                <div className='modal__body--row'>
                    <InputNumber
                        id='standardSeats-input'
                        label={`${translation('TENANT_LICENSE_STANDARD_SEATS')}`}
                        name='standardSeats'
                        value={standardSeats}
                        onValueChange={onFormChange}
                        showButtons
                        idecrementButtonClassName={''}
                        min={minNumberInput}
                        max={maxNumberInput}
                        errorMessage={standardSeatsError ? `${minNumberInput}-${maxNumberInput}` : undefined}
                    />
                </div>
                <div className='modal__body--row'>
                    <InputNumber
                        id='premiumSeats-input'
                        label={`${translation('TENANT_LICENSE_PREMIUM_SEATS')}`}
                        name='premiumSeats'
                        value={premiumSeats}
                        onValueChange={onFormChange}
                        showButtons
                        idecrementButtonClassName={''}
                        min={minNumberInput}
                        max={maxNumberInput}
                        errorMessage={premiumSeatsError ? `${minNumberInput}-${maxNumberInput}` : undefined}
                    />
                </div>
                <div className='modal__body--row'>
                    <InputNumber
                        id='organizationLimit-input'
                        label={`${translation('ORGANIZATION_LIMIT')}`}
                        name='organizationLimit'
                        value={organizationLimit}
                        onValueChange={onFormChange}
                        showButtons
                        idecrementButtonClassName={''}
                        min={minNumberInput}
                        max={maxNumberInput}
                        errorMessage={organizationLimitError ? `${minNumberInput}-${maxNumberInput}` : undefined}
                    />
                </div>
                <div className='modal__body--row'>
                    <Calendar
                        name='expirationDate'
                        label={translation('TENANT_LICENSE_EXPIRATION')}
                        value={expirationDate}
                        onChange={handleDateChange}
                        appendTo={document.body}
                        baseZIndex={10000}
                        showWeek={true}
                        monthNavigator={true}
                        yearNavigator={true}
                        yearRange={'2020:2100'}
                    />
                </div>
            </form>

            {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={formInvalid}
                id='editTenantLicense'
            />
        </>
    );
};
